 // =================================================================
 // RAÍZ CUADRADA — PHONE CODES LIBRARY
 // Utility Functions for Phone Validation and Formatting
 // =================================================================
 
 import { PhoneCode, InternalCode, PhoneValidationResult } from './types';
 import { COMMON_PHONE_CODES, MEXICO_INTERNAL_CODES, SPECIAL_CODES } from './constants';
 
 export class PhoneUtils {
   /**
    * Normaliza un número telefónico a formato E.164
    */
   static normalizePhoneNumber(phone: string, countryCode: string = '+52'): string {
     // Remover todos los caracteres no numéricos excepto +
     let normalized = phone.replace(/[^\d+]/g, '');
     
     // Si comienza con 00 (prefijo internacional), reemplazar con +
     if (normalized.startsWith('00')) {
       normalized = '+' + normalized.substring(2);
     }
     
     // Si no tiene prefijo internacional, agregar el código del país
     if (!normalized.startsWith('+')) {
       // Remover prefijo nacional (01) si existe
       if (normalized.startsWith('01')) {
         normalized = normalized.substring(2);
       }
       // Remover prefijo móvil (044/045) si existe
       if (normalized.startsWith('044') || normalized.startsWith('045')) {
         normalized = normalized.substring(3);
       }
       // Agregar código del país
       normalized = countryCode + normalized;
     }
     
     return normalized;
   }
   
   /**
    * Valida y parsea un número telefónico
    */
   static validatePhoneNumber(phone: string, expectedCountryCode?: string): PhoneValidationResult {
     const cleanPhone = phone.replace(/\s/g, '');
     
     // Si está vacío
     if (!cleanPhone) {
       return {
         isValid: false,
         isMobile: false,
         normalizedNumber: '',
         countryCode: '',
         localNumber: '',
         errorMessage: 'El número de teléfono es requerido',
       };
     }
     
     const normalized = this.normalizePhoneNumber(phone, expectedCountryCode);
     
     // Extraer código del país
     const countryMatch = normalized.match(/^\+(\d{1,4})/);
     if (!countryMatch) {
       return {
         isValid: false,
         isMobile: false,
         normalizedNumber: normalized,
         countryCode: '',
         localNumber: normalized,
         errorMessage: 'Código de país inválido',
       };
     }
     
     const dialCode = countryMatch[1];
     const country = this.findCountryByDialCode(dialCode);
     
     // Validar específicamente para México
     if (dialCode === '52') {
       return this.validateMexicanPhoneNumber(phone);
     }
     
     // Validación genérica internacional
     const localNumber = normalized.replace(`+${dialCode}`, '');
     const isValidLength = localNumber.length >= 7 && localNumber.length <= 15;
     
     return {
       isValid: isValidLength,
       isMobile: false,
       normalizedNumber: normalized,
       countryCode: `+${dialCode}`,
       localNumber,
       country,
       errorMessage: isValidLength ? undefined : 'Longitud de número inválida',
     };
   }
   
   /**
    * Encuentra un país por su código de marcación
    */
   private static findCountryByDialCode(dialCode: string): PhoneCode | undefined {
     return COMMON_PHONE_CODES.find(c => c.dialCode === dialCode);
   }
   
   /**
    * Valida específicamente números mexicanos
    */
   static validateMexicanPhoneNumber(phone: string): PhoneValidationResult {
     let cleanNumber = phone.replace(/\D/g, '');
     
     // Remover el código de país si existe
     if (cleanNumber.startsWith('52')) {
       cleanNumber = cleanNumber.substring(2);
     }
     if (cleanNumber.startsWith('521')) {
       cleanNumber = cleanNumber.substring(3);
     }
     
     // Remover prefijos móviles antiguos
     if (cleanNumber.startsWith('044') || cleanNumber.startsWith('045')) {
       cleanNumber = cleanNumber.substring(3);
     }
     if (cleanNumber.startsWith('01')) {
       cleanNumber = cleanNumber.substring(2);
     }
     
     // Es número de emergencia?
     if ((SPECIAL_CODES.EMERGENCY as readonly string[]).includes(cleanNumber)) {
       return {
         isValid: true,
         isMobile: false,
         normalizedNumber: cleanNumber,
         countryCode: '+52',
         localNumber: cleanNumber,
         country: COMMON_PHONE_CODES[0],
       };
     }
     
     // Validar longitud (10 dígitos para México)
     if (cleanNumber.length !== 10) {
       return {
         isValid: false,
         isMobile: false,
         normalizedNumber: `+52${cleanNumber}`,
         countryCode: '+52',
         localNumber: cleanNumber,
         country: COMMON_PHONE_CODES[0],
         errorMessage: cleanNumber.length < 10 
           ? `Faltan ${10 - cleanNumber.length} dígitos`
           : `Sobran ${cleanNumber.length - 10} dígitos`,
       };
     }
     
     // Determinar lada (primeros 2 o 3 dígitos)
     let areaCode = '';
     let localNumber = '';
     let internalCode: InternalCode | undefined;
     
     // Intentar con 2 dígitos primero
     const twoDigitCode = cleanNumber.substring(0, 2);
     internalCode = MEXICO_INTERNAL_CODES.find(c => c.code === twoDigitCode);
     
     if (internalCode) {
       areaCode = twoDigitCode;
       localNumber = cleanNumber.substring(2);
     } else {
       // Intentar con 3 dígitos
       const threeDigitCode = cleanNumber.substring(0, 3);
       internalCode = MEXICO_INTERNAL_CODES.find(c => c.code === threeDigitCode);
       
       if (internalCode) {
         areaCode = threeDigitCode;
         localNumber = cleanNumber.substring(3);
       } else {
         // No encontramos lada conocida, pero puede ser válido
         areaCode = twoDigitCode;
         localNumber = cleanNumber.substring(2);
       }
     }
     
     // Móviles en México generalmente empiezan con 1 después de la lada (legacy)
     // Pero desde 2019 todos son 10 dígitos, asumimos móvil para propósitos de WhatsApp
     const isMobile = true;
     
     return {
       isValid: true,
       isMobile,
       normalizedNumber: `+52${cleanNumber}`,
       countryCode: '+52',
       areaCode,
       localNumber,
       country: COMMON_PHONE_CODES[0],
       internalCode,
     };
   }
   
   /**
    * Formatea un número telefónico para display
    */
   static formatPhoneNumber(
     phone: string, 
     format: 'international' | 'national' | 'whatsapp' = 'national'
   ): string {
     const validation = this.validatePhoneNumber(phone);
     
     if (!validation.isValid) {
       return phone;
     }
     
     const { countryCode, areaCode, localNumber, normalizedNumber } = validation;
     
     switch (format) {
       case 'international':
         if (countryCode === '+52' && areaCode) {
           const formattedLocal = localNumber.length === 8 
             ? `${localNumber.substring(0, 4)} ${localNumber.substring(4)}`
             : localNumber;
           return `${countryCode} ${areaCode} ${formattedLocal}`;
         }
         return normalizedNumber;
       
       case 'national':
         if (countryCode === '+52' && areaCode) {
           const formattedLocal = localNumber.length === 8 
             ? `${localNumber.substring(0, 4)} ${localNumber.substring(4)}`
             : localNumber;
           return `(${areaCode}) ${formattedLocal}`;
         }
         return validation.localNumber;
       
       case 'whatsapp':
         // WhatsApp format: sin espacios ni símbolos
         return normalizedNumber.replace(/\D/g, '');
       
       default:
         return phone;
     }
   }
   
   /**
    * Obtiene información de un código de área mexicano
    */
   static getAreaCodeInfo(code: string): InternalCode | undefined {
     return MEXICO_INTERNAL_CODES.find(ac => ac.code === code);
   }
   
   /**
    * Obtiene información de un código de país
    */
   static getCountryInfo(iso2OrDialCode: string): PhoneCode | undefined {
     return COMMON_PHONE_CODES.find(c => 
       c.iso2 === iso2OrDialCode.toUpperCase() || 
       c.dialCode === iso2OrDialCode
     );
   }
   
   /**
    * Obtiene lista de países ordenados
    */
   static getSortedCountries(sortBy: 'name' | 'code' = 'name'): PhoneCode[] {
     const countries = [...COMMON_PHONE_CODES];
     
     if (sortBy === 'name') {
       return countries.sort((a, b) => a.country.localeCompare(b.country, 'es'));
     }
     
     return countries.sort((a, b) => parseInt(a.dialCode) - parseInt(b.dialCode));
   }
   
   /**
    * Detecta automáticamente el país basado en el número
    */
   static detectCountry(phone: string): PhoneCode | undefined {
     const cleanPhone = phone.replace(/\D/g, '');
     
     // Buscar por código de marcación
     for (const country of COMMON_PHONE_CODES) {
       if (cleanPhone.startsWith(country.dialCode)) {
         return country;
       }
     }
     
     // Si no tiene código de país, asumimos México
     return COMMON_PHONE_CODES.find(c => c.iso2 === 'MX');
   }
 }